// Informe de conducción (especificación §2) para la vista «Ver conducción».
// Es el MISMO cálculo que el archivo PDF/Excel:
//   apps/torre-de-control-api/functions/src/modules/reports/reports/drivingReportRule.js
//   (+ utils/idleRule.js y utils/vehicleParamsResolver.js)
// Si cambias uno, cambia el otro: drivingReportRule.test.ts repite los casos
// de drivingReportRule.unit.test.js del backend.
import { resolveMetricsFromParams, type RawParams } from "../../../utils/vehicleParamsResolver";
import type { RawHistoryPoint } from "../utils/trackingHistoryWindows";
import { cleanOdometer, isDisplaced } from "../utils/intervalDistance";

export const MOVEMENT_THRESHOLD_KMH = 3;
export const MAX_IDLE_GAP_MS = 15 * 60 * 1000;
// Rachas de Conducción: el corte de Informes rápidos (30 min), como
// REPORT_GAP_MS de utils/reportStatuses.js del API (decisión 21-oct).
export const REPORT_GAP_MS = 30 * 60 * 1000;
// Por debajo, un avance chico del odómetro se amplifica a km/h absurdos
// (10 m en 1 s = 36 km/h).
const MIN_IMPLIED_SPEED_DELTA_MS = 3000;
// RPM 65535 (0xFFFF) = «no disponible» (los 5 equipos que mandan `rpm`, 16-oct).
const RPM_KEYS = ["rpm", "can_engine_speed", "obd_engine_rpm", "engine_rpm"];
const RPM_INVALID = 65535;

export interface DrivingPoint {
  ts: number;
  speed: number | null;
  rpm: number | null;
  ignition: boolean;
  engineStatus: number | null;
  idle: boolean | null;
  odoKm: number | null;
  lat: number;
  lon: number;
  eventCode: string;
  provider: string | null;
}

export interface DrivingRuleRow {
  point: DrivingPoint;
  speed: number | null;
  ralenti: boolean;
  eventCode: string;
}

export interface DrivingRuleResult {
  rows: DrivingRuleRow[];
  totalPoints: number;
  idleCount: number;
  /** segundos */
  totalIdleTime: number;
}

type Params = Record<string, unknown> | null | undefined;

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

// Igual que ReportUtils.resolveOdoKm: km directos o metros / 1000.
export const odoKmOf = (params: Params): number | null => {
  if (!params) return null;
  const km = num(params.odo_km ?? params.odometer_km);
  if (km != null) return km;
  const meters = num(params.virtual_odometer ?? params.total_odometer ?? params.odometer);
  return meters == null ? null : meters / 1000;
};

export const rpmOf = (params: Params): number | null => {
  if (!params) return null;
  for (const key of RPM_KEYS) {
    const v = num(params[key]);
    if (v !== null && v >= 0 && v !== RPM_INVALID) return v;
  }
  return null;
};

/** Punto de GET /tracking/history al contrato del cálculo (como drivingReport.js). */
export const toDrivingPoint = (
  p: RawHistoryPoint,
  brandName?: string | null,
  brandModel?: string | null,
): DrivingPoint => {
  const params = (p.params ?? null) as Record<string, unknown> | null;
  const ign = p.ignition as unknown;
  const idle =
    params && (brandName ?? "").trim().toLowerCase() === "ruptela"
      ? resolveMetricsFromParams(params as RawParams, brandModel, brandName).idle
      : null;
  return {
    ts: p.ts,
    speed: num(p.speed),
    rpm: rpmOf(params),
    ignition: ign === true || ign === 1 || ign === "1",
    engineStatus: num(params?.engine_status),
    idle,
    odoKm: odoKmOf(params),
    lat: num(p.lat) || 0,
    lon: num(p.lng) || 0,
    eventCode: String(p.event_code ?? "6"),
    provider: typeof params?.provider === "string" ? params.provider : null,
  };
};

export const isEngineOn = (ignition: boolean, engineStatus: number | null): boolean =>
  ignition === true || engineStatus === 1 || engineStatus === 2 || engineStatus === 3;

/** §2.1: la velocidad del punto o, si no trae, la estimada por odómetro. */
export const effectiveSpeeds = (points: DrivingPoint[]): (number | null)[] =>
  points.map((p, i) => {
    if (p.speed !== null) return p.speed;
    if (i === 0) return null;
    const prev = points[i - 1];
    if (prev.odoKm == null || p.odoKm == null || !prev.ts || !p.ts) return null;
    const dKm = p.odoKm - prev.odoKm;
    const dMs = p.ts - prev.ts;
    if (dKm <= 0 || dMs < MIN_IMPLIED_SPEED_DELTA_MS) return null;
    return dKm / (dMs / 3600000);
  });

type Base = "driving" | "ralenti" | "stopped" | "idle";

const baseState = (speed: number | null, p: DrivingPoint): Base => {
  if (speed !== null && speed > MOVEMENT_THRESHOLD_KMH) return "driving";
  if (p.idle === true) return "ralenti";
  if (p.idle === false) return "stopped";
  return isEngineOn(p.ignition, p.engineStatus) ? "idle" : "stopped";
};

/** Estado final: rachas de «motor encendido quieto» (huecos <= 15 min) pasan a ralentí. */
export const finalStatuses = (points: DrivingPoint[], speeds = effectiveSpeeds(points)): string[] => {
  const base = points.map((p, i) => baseState(speeds[i], p));
  const final = base.map((b) => (b === "idle" ? "stopped" : b));
  let i = 0;
  while (i < points.length) {
    if (base[i] !== "idle") {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < points.length && base[j] === "idle" && points[j].ts - points[j - 1].ts <= MAX_IDLE_GAP_MS) j++;
    for (let k = i; k < j; k++) final[k] = "ralenti";
    i = j;
  }
  return final;
};

// Mapon estampa event_code=1 («Alerta de velocidad» en el catálogo) en TODA
// posición como reporte periódico: es una posición.
export const eventCodeOf = (p: DrivingPoint): string =>
  p.provider === "mapon" && p.eventCode === "1" ? "6" : p.eventCode;

/** Como displacedStatuses del API: «manejando» sin desplazamiento real es ralentí (motor encendido) o detenido. */
export const displacedStatuses = (points: DrivingPoint[], statuses: string[]): string[] => {
  const pts = points.map((p) => ({ ts: p.ts, lat: p.lat, lon: p.lon, odoKm: p.odoKm }));
  const odo = cleanOdometer(pts);
  return statuses.map((s, i) => (s !== "driving" || isDisplaced(pts, odo.anchorAt, i) ? s : points[i].ignition ? "ralenti" : "stopped"));
};

export const buildDrivingReport = (
  points: DrivingPoint[],
  { maxSpeed = 120, maxRpm = 2500 }: { maxSpeed?: number; maxRpm?: number } = {},
): DrivingRuleResult => {
  const speeds = effectiveSpeeds(points);
  const statuses = displacedStatuses(points, finalStatuses(points, speeds));

  let idleCount = 0;
  let totalIdleTime = 0;
  let i = 0;
  while (i < points.length) {
    if (statuses[i] !== "ralenti") {
      i++;
      continue;
    }
    let j = i + 1;
    while (j < points.length && statuses[j] === "ralenti" && points[j].ts - points[j - 1].ts <= REPORT_GAP_MS) j++;
    idleCount++;
    totalIdleTime += (points[j - 1].ts - points[i].ts) / 1000;
    i = j;
  }

  const rows: DrivingRuleRow[] = [];
  points.forEach((p, k) => {
    const speed = speeds[k];
    if (speed !== null && speed >= maxSpeed) return;
    if (p.rpm !== null && p.rpm >= maxRpm) return;
    rows.push({ point: p, speed, ralenti: statuses[k] === "ralenti", eventCode: eventCodeOf(p) });
  });

  return { rows, totalPoints: rows.length, idleCount, totalIdleTime };
};
